import React, {useRef, useState} from 'react'
import {useActiveStore} from './StoreContext'
import {useClickAway} from '../hooks/useClickAway'
import {StoreList} from './StoreList'
import {RoleTag, storeLabel} from './StoreTags'
import styles from './StoreSwitcher.module.scss'

type StoreSwitcherProps = {
  /**
   * Called with the chosen store id. Selecting a store is a route navigation
   * (StoreContext.switchStore → /page/:storeCode), so the switcher never mutates
   * store state directly; the shell's unsaved-changes blocker guards the switch.
   */
  onSelect: (storeId: number) => void
}

/**
 * Store switcher. The store name + code is the prominent piece of the
 * top bar's right cluster — a store manager's context. It is the single store
 * control across breakpoints: inline in the top bar on desktop, and a full-width
 * selector row (the per-page store selector) below the top bar on mobile.
 *
 * - Multiple stores: an affordanced dropdown — a bordered control with a clear
 * CSS chevron; the panel is a searchable StoreList (search appears from 8
 * stores upwards) and is constrained to the viewport so a long list never clips.
 * - Single store: the store name (prominent) plus its role, non-interactive.
 * - Loading / no stores: renders nothing.
 *
 * Both branches show the ACTIVE store's role beside the control: a read-only
 * user has to know they are one without opening anything, whether they hold one
 * store or twenty. The tag sits OUTSIDE the trigger button on purpose — inside,
 * it becomes part of the button's accessible name ("Tienda Norte (N2) Solo
 * lectura"), which specs and selectors read.
 */
export const StoreSwitcher = ({onSelect}: StoreSwitcherProps): React.ReactElement | null => {
  const {activeStore, stores, storeStatus} = useActiveStore()
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  useClickAway(ref, () => setOpen(false), open)

  if (stores.length === 0) return null

  if (stores.length === 1) {
    return (
      <div className={`row-flx flx-a-c flx-gap-8 ${styles['store-switcher']}`}>
        <span className={`txt-upper txt-bold-500 txt-light-kale ${styles['store-switcher__eyebrow']}`}>Tienda</span>
        <span className={`txt-kale ${styles['store-switcher__single']}`}>{storeLabel(stores[0])}</span>
        <RoleTag role={stores[0].role} />
      </div>
    )
  }

  return (
    <div className={`row-flx flx-a-c flx-gap-8 ${styles['store-switcher']}`} ref={ref}>
      <span className={`txt-upper txt-bold-500 txt-light-kale ${styles['store-switcher__eyebrow']}`}>Tienda</span>
      <div className={`modul ${styles['store-switcher__control']}`}>
        <button
          type="button"
          className={`row-flx flx-a-c flx-gap-8 border-xxl bg-white ${styles['store-switcher__trigger']}`}
          onClick={() => setOpen((prev) => !prev)}
          aria-haspopup="listbox"
          aria-expanded={open}
        >
          <span className={`txt-kale ${styles['store-switcher__current']}`}>
            {activeStore !== undefined ? storeLabel(activeStore) : 'Seleccionar tienda'}
          </span>
          <span
            className={[
              'itm-flx-s-0',
              styles['store-switcher__chevron'],
              open ? styles['store-switcher__chevron--open'] : '',
            ]
              .filter(Boolean)
              .join(' ')}
            aria-hidden="true"
          />
        </button>

        {open && (
          <div className={`border-s bg-white ${styles['store-switcher__panel']}`}>
            <StoreList
              stores={stores}
              activeStoreId={activeStore?.id}
              onSelect={(storeId) => {
                onSelect(storeId)
                setOpen(false)
              }}
              storeStatus={storeStatus}
              autoFocusSearch
            />
          </div>
        )}
      </div>
      {activeStore !== undefined && <RoleTag role={activeStore.role} />}
    </div>
  )
}
